// Meters for files being uploaded, shown at the place they will go (the caret
// when they were chosen).
//
// Why there: a band at the bottom of the body was always visible even in long
// documents, but the place chosen and the place shown were far apart, so it was
// rejected ("the meter should appear where the cursor was").
//
// Not a decoration: nothing in flight is written into the document
// (file-upload.ts) - a widget would vanish as soon as someone else typed. The
// remembered place (a Yjs relative position) is resolved through
// `PendingFileUpload.pos()` and the meter is drawn there with
// `position: fixed`, inside the document's area (not portaled; the theme
// tokens live on <html> anyway, and `fixed` escapes the scroll container's
// clipping). Assumes no ancestor with transform / filter / contain (they would
// trap `fixed`); there is none between the panel and the root today.
//
// The layer stays low: the menus over the body are above it (z-index 50).
//
// Meters do not close on scroll or resize (that would hide work in progress);
// they are measured again on scroll, resize and document changes. The store
// holds only the list; positions are measured from the DOM on every render.
//
// Look: the same shape as other attachments, with ground and shadow because it
// floats over the body. Progress is a thin growing line, not a number (calm;
// no rushing). The line sits inside the chip (along the bottom edge it would
// meet the rounded corners square). A failure shows its reason in color and
// keeps the row (cancel and paste again). Name and size; the size only while
// in flight. No `title` (tooltips are not shown).
import type { Editor } from "@tiptap/core";
import { m } from "@trameria/i18n";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { Icon } from "../../ui/icon";
import type { PendingFileUpload } from "./file-upload";
import { formatBytes } from "./upload";

/** One meter's height (stacking needs it as a value; also its `height`). */
export const METER_HEIGHT = 26;
/** The gap to the caret's line and between stacked meters. */
const GAP = 4;
/** Wide enough to read some of the name; not moved further right than this allows. */
const MIN_WIDTH = 180;
/** The viewport margin, as popovers keep. */
const EDGE = 8;

interface Box {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface MeterAnchor {
  key: string;
  /** The insert place's coordinates (viewport), or null when gone or not drawn yet. */
  at: { top: number; bottom: number; left: number } | null;
}

export interface MeterPlacement {
  key: string;
  left: number;
  top: number;
  /** Width up to the viewport's right edge: the name inside shrinks (the meter does not jump left). */
  maxWidth: number;
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/**
 * Where the meters go (pure: position tests aim here).
 * - Within the visible band of the editing area: popover clamping knows only
 *   the viewport, this needs the area's visible top and bottom.
 * - A meter without a place (`at` null) goes to the visible bottom; so does
 *   one scrolled out of view - scrolling right after choosing must not look
 *   like "nothing happened".
 * - Meters in flight together stack, never overlap, and room for the ones
 *   below is kept first, so all fit even at the bottom.
 */
export function placeUploadMeters({
  anchors,
  area,
  viewport,
  height = METER_HEIGHT,
  gap = GAP,
}: {
  anchors: MeterAnchor[];
  /** The editing area's visible rectangle (after clipping ancestors). */
  area: Box;
  viewport: { width: number; height: number };
  height?: number;
  gap?: number;
}): MeterPlacement[] {
  // The area within the viewport; if not even one meter fits, the viewport.
  let minTop = Math.max(area.top, EDGE);
  let maxTop = Math.min(area.bottom, viewport.height - EDGE) - height;
  if (maxTop < minTop) {
    minTop = EDGE;
    maxTop = Math.max(EDGE, viewport.height - EDGE - height);
  }
  const step = height + gap;
  // Stacked from the top; two at one place keep the order chosen (stable sort).
  const wanted = anchors.map((anchor, index) => ({
    anchor,
    index,
    want: anchor.at === null ? maxTop : anchor.at.bottom + gap,
  }));
  const placements: MeterPlacement[] = [];
  let taken = Number.NEGATIVE_INFINITY;
  for (const [rank, { anchor, index, want }] of wanted
    .slice()
    .sort((a, b) => a.want - b.want)
    .entries()) {
    const limit = Math.max(minTop, maxTop - (wanted.length - 1 - rank) * step);
    const top = Math.max(clamp(want, minTop, limit), taken);
    taken = top + step;
    // Without a place, the body column's left edge (stays in the text's flow).
    const left = clamp(
      anchor.at?.left ?? area.left,
      EDGE,
      viewport.width - EDGE - MIN_WIDTH,
    );
    placements[index] = {
      key: anchor.key,
      left,
      top,
      maxWidth: Math.max(0, viewport.width - EDGE - left),
    };
  }
  return placements;
}

/** The empty list for an editor without uploads (one reference, or it re-renders forever). */
const NONE: PendingFileUpload[] = [];

function noSubscribe(): () => void {
  return () => {};
}

/**
 * The insert place's coordinates. `coordsAtPos` throws out of range, so the
 * range is checked and a throw still becomes null (the document and the view
 * can disagree for a moment).
 */
function anchorAt(editor: Editor, pos: number | null): MeterAnchor["at"] {
  if (pos === null || !Number.isInteger(pos)) return null;
  if (pos < 0 || pos > editor.state.doc.content.size) return null;
  try {
    const { top, bottom, left } = editor.view.coordsAtPos(pos);
    return { top, bottom, left };
  } catch {
    return null;
  }
}

/**
 * Ancestors that clip the body (the scroll container and outer ones hiding
 * overflow). `editor.view.dom`'s rectangle includes what scrolled out, so
 * without them a long document scrolled down put the meter above the area -
 * over the header. Looked up when shown, not on every scroll. Stops before
 * <body>: the viewport clamp is done by `placeUploadMeters`.
 */
function clippingParents(dom: HTMLElement): HTMLElement[] {
  const found: HTMLElement[] = [];
  for (let el = dom.parentElement; el && el !== document.body; ) {
    const overflow = getComputedStyle(el).overflowY;
    if (overflow && overflow !== "visible") found.push(el);
    el = el.parentElement;
  }
  return found;
}

/** The editing area's visible rectangle (`editor.view.dom` within its clipping ancestors). */
function visibleArea(dom: HTMLElement, clips: HTMLElement[]): Box {
  let box: Box = dom.getBoundingClientRect();
  for (const clip of clips) {
    const r = clip.getBoundingClientRect();
    box = {
      top: Math.max(box.top, r.top),
      bottom: Math.min(box.bottom, r.bottom),
      left: Math.max(box.left, r.left),
      right: Math.min(box.right, r.right),
    };
  }
  return box;
}

export function FileUploadMeters({ editor }: { editor: Editor | null }) {
  const storage = editor?.storage.fileUpload;
  const uploads = useSyncExternalStore(
    storage?.subscribe ?? noSubscribe,
    () => storage?.uploads ?? NONE,
    () => NONE,
  );
  // A re-measure signal, separate from the list: positions move without the
  // list changing (scroll, resize, document changes).
  const [, setTick] = useState(0);
  const remeasure = useCallback(() => setTick((n) => n + 1), []);
  const shown = uploads.length > 0;
  const clips = useRef<HTMLElement[]>([]);

  // Before drawing (layout effect), so the first frame already knows the
  // clipping ancestors.
  // frontend-development: not a fetch; follows scroll, resize and edits while meters show.
  useLayoutEffect(() => {
    if (!editor || !shown) return;
    clips.current = clippingParents(editor.view.dom);
    remeasure();
    // Capture: the body's own scroll container too. Edits move the place
    // itself (own and remote), so transactions re-measure as well.
    window.addEventListener("scroll", remeasure, true);
    window.addEventListener("resize", remeasure);
    editor.on("transaction", remeasure);
    return () => {
      window.removeEventListener("scroll", remeasure, true);
      window.removeEventListener("resize", remeasure);
      editor.off("transaction", remeasure);
    };
  }, [editor, shown, remeasure]);

  if (!editor || uploads.length === 0) return null;

  // Measured on every render. Its own size is not measured: the height is
  // METER_HEIGHT and the width is capped by `maxWidth`, so no hidden first
  // frame is needed.
  const placements = placeUploadMeters({
    anchors: uploads.map((u) => ({
      key: u.key,
      at: anchorAt(editor, u.pos()),
    })),
    area: visibleArea(editor.view.dom, clips.current),
    viewport: {
      width: window.innerWidth || 0,
      height: window.innerHeight || 0,
    },
  });

  return (
    // Each meter is `fixed` on its own; the list takes no space and holds the layer.
    <ul
      aria-label={m.blocks_uploads()}
      className="fixed top-0 left-0 z-10 m-0 h-0 w-0 list-none p-0"
    >
      {uploads.map((u, index) => {
        const at = placements[index];
        return (
          <li
            key={u.key}
            style={{
              left: at.left,
              top: at.top,
              maxWidth: at.maxWidth,
              height: METER_HEIGHT,
            }}
            className="fixed flex items-center gap-2 overflow-hidden rounded-[5px] border border-border bg-surface pr-1 pl-2 text-[12.5px] text-fg shadow-[var(--shadow-popover)]"
          >
            <span className="truncate">{u.name}</span>
            {u.error ? (
              <span className="flex-none text-danger">{u.error}</span>
            ) : (
              <span className="flex-none text-faint tabular-nums">
                {formatBytes(u.size)}
              </span>
            )}
            <button
              type="button"
              aria-label={m.blocks_upload_cancel({ name: u.name })}
              // Keeps the body's selection and focus; the icon takes no hits.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => u.cancel()}
              className="flex-none cursor-pointer rounded-[4px] p-0.5 text-muted hover:bg-hover hover:text-body"
            >
              <Icon kind="close" size={14} />
            </button>
            {!u.error && (
              <span
                data-upload-progress=""
                className="pointer-events-none absolute inset-x-2 bottom-[3px] h-[2px]"
              >
                <span
                  className="block h-full rounded-full bg-accent transition-[width] duration-150"
                  style={{ width: `${Math.round(u.progress * 100)}%` }}
                />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
